// Smart Help Backend Configuration
export const SMART_HELP_CONFIG = {
  // Backend service URL - can be configured via environment variables
  BASE_URL: process.env.REACT_APP_SMART_HELP_URL || 'http://localhost:3001',

  // API Endpoints (matching backend controller)
  ENDPOINTS: {
    CHAT: '/api/v1/folio-help/chat',
    SEARCH: '/api/v1/folio-help/search',
    HEALTH: '/api/v1/folio-help/health',
    SESSIONS: '/api/v1/folio-help/chat/sessions',
    CLEAR_SESSION: '/api/v1/folio-help/chat/sessions',
    DOCUMENTS_STATUS: '/api/v1/folio-help/documents/status',
    SYSTEM_METRICS: '/api/v1/folio-help/system/metrics',
  },

  // Request timeout in milliseconds (increased for slow backend)
  TIMEOUT: 60000,

  // Headers
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
  },
};

// Helper function to build full URL
export const buildApiUrl = (endpoint) => {
  return `${SMART_HELP_CONFIG.BASE_URL}${endpoint}`;
};

// API service for Smart Help backend
export class SmartHelpAPI {
  static async post(endpoint, data, options = {}) {
    const url = buildApiUrl(endpoint);

    const config = {
      method: 'POST',
      headers: {
        ...SMART_HELP_CONFIG.DEFAULT_HEADERS,
        ...options.headers,
      },
      body: JSON.stringify(data),
      signal: options.signal,
      ...options,
    };

    try {
      console.log('Making request to:', url);
      const response = await fetch(url, config);

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error body:', errorText);
        throw new Error(
          `HTTP error! status: ${response.status}, body: ${errorText}`,
        );
      }

      const result = await response.json();
      console.log('✅ Response received successfully');
      return result;
    } catch (error) {
      console.error('Smart Help API Error:', error);
      throw error;
    }
  }

  static async get(endpoint, options = {}) {
    const url = buildApiUrl(endpoint);

    const config = {
      method: 'GET',
      headers: {
        ...SMART_HELP_CONFIG.DEFAULT_HEADERS,
        ...options.headers,
      },
      signal: options.signal,
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Smart Help API Error:', error);
      throw error;
    }
  }

  // Health check endpoint
  static async healthCheck() {
    try {
      return await this.get(SMART_HELP_CONFIG.ENDPOINTS.HEALTH);
    } catch (error) {
      return { status: 'error', error: error.message };
    }
  }

  // Chat endpoint with proper payload structure
  static async sendMessage(
    message,
    sessionId = null,
    userId = null,
    options = {},
  ) {
    const payload = {
      message,
      sessionId:
        sessionId ||
        `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      userId: userId || 'anonymous',
    };

    console.log('Sending payload to backend:', payload);

    return await this.post(SMART_HELP_CONFIG.ENDPOINTS.CHAT, payload, options);
  }

  // Search endpoint
  static async search(query, limit = 10, scoreThreshold = 0.5, options = {}) {
    const payload = {
      query,
      limit,
      scoreThreshold,
    };

    return await this.post(
      SMART_HELP_CONFIG.ENDPOINTS.SEARCH,
      payload,
      options,
    );
  }

  // Get chat sessions
  static async getSessions(options = {}) {
    return await this.get(SMART_HELP_CONFIG.ENDPOINTS.SESSIONS, options);
  }

  // Clear session
  static async clearSession(sessionId, options = {}) {
    return await this.post(
      `${SMART_HELP_CONFIG.ENDPOINTS.CLEAR_SESSION}/${sessionId}/clear`,
      {},
      options,
    );
  }

  // Get documents status
  static async getDocumentsStatus(options = {}) {
    return await this.get(
      SMART_HELP_CONFIG.ENDPOINTS.DOCUMENTS_STATUS,
      options,
    );
  }

  // Get system metrics
  static async getSystemMetrics(options = {}) {
    return await this.get(SMART_HELP_CONFIG.ENDPOINTS.SYSTEM_METRICS, options);
  }
}
